/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Row, Col } from "react-awesome-styled-grid";
import { Link, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { Flex, Spacer, Table, Text, theme } from "@nordcloud/gnui";
import { ApplicationEnvironmentDelete } from "~/components/Applications/ApplicationEnvironmentDelete";
import { ApplicationRemove } from "~/components/Applications/ApplicationRemove";
import {
  AdditionalInfo,
  CollapseItem,
} from "~/components/CollapseItem/CollapseItem";
import { Popover } from "~/components/Popover/Popover";
import { PopoverButton } from "~/components/Popover/PopoverButton";
import { PopoverMenuButton } from "~/components/Popover/PopoverMenuButton";
import FlexContainer from "~/components/Styled/FlexContainer";
import { dateFormatWithHours } from "~/constants";
import { Application, ApplicationEnv, RouteParams } from "~/models";
import { ROUTES } from "~/routing";
import { displayEnvStatus, displayEnvType, displayLevel } from "~/tools";

type ApplicationItemProps = {
  application: Application;
  fetchApplications: () => void;
};

export const TagWrap = styled.div`
  transform: translateY(0.7rem);
`;

const RemovedText = styled(Text)`
  cursor: not-allowed;
  font-weight: lighter;
`;

export function ApplicationItem({
  application,
  fetchApplications,
}: ApplicationItemProps) {
  const state = useParams<RouteParams>();
  const { customerId } = state;

  const [isPopover, setIsPopover] = React.useState(false);

  const history = useHistory();

  return (
    <>
      <CollapseItem
        isRemoved={application.removed}
        name={
          <FlexContainer grow>
            <FlexContainer direction="column" align="flex-start">
              <Text
                size="lg"
                weight="medium"
                tag="div"
                mb={theme.spacing.spacing01}
              >
                {application.name}
              </Text>
              <Flex>
                <Text size="sm" tag="span">
                  {application.environments?.length || 0} environments
                </Text>
              </Flex>
            </FlexContainer>
            <AdditionalInfo>
              {application.removed && (
                <Text size="sm" weight="medium" tag="div">
                  Removed on{" "}
                  {dayjs(application?.synchronizedAt).format(
                    `${dateFormatWithHours}`
                  )}
                </Text>
              )}
            </AdditionalInfo>
          </FlexContainer>
        }
        buttons={
          <Popover
            alignRight
            setIsPopover={setIsPopover}
            isPopover={isPopover}
            trigger={<PopoverButton />}
          >
            <PopoverMenuButton
              icon="edit"
              onClick={() => {
                history.push(
                  `${ROUTES.customers.index}/${customerId}/applications/${application.id}`
                );
              }}
            >
              Edit
            </PopoverMenuButton>
            <ApplicationRemove
              application={application}
              setIsPopover={setIsPopover}
              refetch={fetchApplications}
            />
          </Popover>
        }
      >
        <Row>
          <Col>
            <Spacer height="1rem" />
            <Table small>
              <Table.thead>
                <Table.tr>
                  <Table.th>Environment</Table.th>
                  <Table.th width="18%">Type</Table.th>
                  <Table.th width="18%">Status</Table.th>
                  <Table.th width="18%">Service level</Table.th>
                </Table.tr>
              </Table.thead>
              <Table.tbody>
                {application?.environments?.map(
                  (environment: ApplicationEnv) => {
                    return (
                      <Table.tr
                        key={environment?.id}
                        css={{
                          opacity:
                            environment.removed && !application.removed
                              ? 0.5
                              : 1,
                        }}
                      >
                        {!environment.removed ? (
                          <>
                            <Table.td>
                              <Link
                                to={`${ROUTES.customers.index}/${customerId}/${application.id}/${environment.id}`}
                              >
                                {environment.name}
                              </Link>
                            </Table.td>
                            <Table.td>
                              <TagWrap>
                                {displayEnvType(environment.type)}
                              </TagWrap>
                            </Table.td>
                            <Table.td>
                              <TagWrap>
                                {displayEnvStatus(environment.status)}
                              </TagWrap>
                            </Table.td>
                            <Table.td>
                              <TagWrap>
                                {displayLevel(
                                  environment?.attachedServiceLevel
                                    ?.serviceLevel?.name
                                )}
                              </TagWrap>
                            </Table.td>
                          </>
                        ) : (
                          <>
                            <Table.td>
                              <RemovedText>{environment.name}</RemovedText>
                            </Table.td>
                            <Table.td css={{ textAlign: "right" }} colSpan={3}>
                              <FlexContainer
                                justify="flex-end"
                                align="flex-end"
                                direction="column"
                              >
                                <Text size="sm" weight="medium" tag="div">
                                  Removed on{" "}
                                  {dayjs(environment?.synchronizedAt).format(
                                    `${dateFormatWithHours}`
                                  )}
                                </Text>
                                <ApplicationEnvironmentDelete
                                  environment={environment}
                                  deleteCallback={fetchApplications}
                                />
                              </FlexContainer>
                            </Table.td>
                          </>
                        )}
                      </Table.tr>
                    );
                  }
                )}
              </Table.tbody>
            </Table>
          </Col>
        </Row>
      </CollapseItem>
    </>
  );
}
